import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Phone, ArrowRight, Home, Sofa, Package, Zap, ShoppingCart, Tag, Trash2, Building,
  Clock, ShieldCheck, Smile, ChevronRight, CheckCircle2, BadgeEuro,
  CalendarCheck, Truck, MessageCircle
} from "lucide-react";
import SEO from "@/components/SEO";
import OurVanSection from "@/components/OurVanSection";
import GoogleReviewPrompt from "@/components/GoogleReviewPrompt";
import GoogleReviewsWidget from "@/components/GoogleReviewsWidget";
import BlogTile from "@/components/BlogTile";
import GoCleaningPromo from "@/components/GoCleaningPromo";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { POSTS_INDEX } from "@/pages/blog/posts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_ICONS = { Package, Home, Building, Trash2, Zap };

const SERVICES_PREVIEW = [
  { icon: "Package", title: "Small Jobs / Single Item", desc: "1-3 items, furniture, IKEA, Marketplace pickups" },
  { icon: "Home", title: "2 Room Move", desc: "Apartments and small houses — sorted in hours" },
  { icon: "Building", title: "Full House Move", desc: "Full property relocations with a 2-man team" },
  { icon: "Trash2", title: "Rubbish Removal", desc: "Junk, old furniture, full property clearouts" },
  { icon: "Zap", title: "Emergency Same-Day", desc: "Need it done NOW? 1-2 hour response" },
];

// Module-level constant — kept out of the component so the same object reference
// is passed to <SEO/> on every render (prevents needless re-renders + Helmet diffs).
const HOMEPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Man With a Van Dublin",
  "provider": { "@id": "https://govans.ie/#business" },
  "serviceType": "Moving Service",
  "areaServed": { "@type": "City", "name": "Dublin" },
  "description": "Fast, affordable man with a van service in Dublin. House removals, furniture delivery, IKEA collection, rubbish removal, same-day emergency service.",
  "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "60", "priceSpecification": { "@type": "PriceSpecification", "minPrice": "60", "priceCurrency": "EUR" } }
};

export default function HomePage() {
  const [formData, setFormData] = useState({ name: "", phone: "", message: "" });
  const [contactConsent, setContactConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quoteData, setQuoteData] = useState({ name: "", phone: "", service: "", pickup: "", dropoff: "", items: "", date: "" });
  const [quoteConsent, setQuoteConsent] = useState(false);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [heroReviewPrompt, setHeroReviewPrompt] = useState(false);
  const [contactReviewPrompt, setContactReviewPrompt] = useState(false);

  const buildWhatsAppUrl = () => {
    const msg = `Hi GoVan! I need a quote:\n\nName: ${quoteData.name}\nPhone: ${quoteData.phone}\nService required: ${quoteData.service || "Not specified"}\nPickup: ${quoteData.pickup}\nDrop-off: ${quoteData.dropoff}\nItems: ${quoteData.items || "Not specified"}\nPreferred date: ${quoteData.date || "Flexible"}`;
    return `https://wa.me/353874199541?text=${encodeURIComponent(msg)}`;
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!contactConsent) {
      toast.error("Please agree to the Privacy Policy before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, email: `${formData.phone}@phone.local` });
      toast.success("Got it! We'll call you back within 1-2 minutes.");
      setFormData({ name: "", phone: "", message: "" });
      setContactConsent(false);
      setContactReviewPrompt(true);
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuote = async (e) => {
    e.preventDefault();
    if (!quoteData.name || !quoteData.phone || !quoteData.service || !quoteData.pickup || !quoteData.dropoff) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!quoteConsent) {
      toast.error("Please agree to the Privacy Policy before submitting.");
      return;
    }
    setQuoteSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: quoteData.name,
        email: `${quoteData.phone}@quote.local`,
        phone: quoteData.phone,
        message: `QUICK QUOTE: ${quoteData.service} | From ${quoteData.pickup} to ${quoteData.dropoff} | Items: ${quoteData.items || 'Not specified'} | Date: ${quoteData.date || 'Flexible'}`,
      });
      toast.success("Quote request sent! We'll call you back within 1-2 minutes.");
      setQuoteData({ name: "", phone: "", service: "", pickup: "", dropoff: "", items: "", date: "" });
      setQuoteConsent(false);
      setHeroReviewPrompt(true);
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setQuoteSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Man With a Van Dublin — Same-Day Moving Service"
        description="GoVans.ie — Dublin's fastest man with a van. House removals, furniture delivery, IKEA collection, same-day service. From €60. Call 087 419 9541."
        canonical="/"
        schema={HOMEPAGE_SCHEMA}
      />
      {/* ===== PHONE BAR ===== */}
      <div data-testid="phone-bar" className="bg-[#0B1E36] text-white py-2">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <span className="text-xs sm:text-sm text-white/60">Dublin&apos;s Man With a Van &mdash; 7 Days a Week</span>
          <a href="tel:+353874199541" className="flex items-center gap-2 text-sm font-bold text-white hover:text-[#0055FF] transition-colors">
            <Phone className="w-4 h-4" />
            <span>087 419 9541</span>
          </a>
        </div>
      </div>

      {/* ===== HERO SECTION ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center overflow-hidden"
      >
        {/* Responsive hero image — separate <picture> for LCP optimisation. Dimensions match the sized assets (mobile 800x450, desktop 1440x810) so there's zero CLS. */}
        <picture>
          <source media="(min-width: 769px)" srcSet="/img/hero-desktop.avif" type="image/avif" width="1440" height="810" />
          <source media="(min-width: 769px)" srcSet="/img/hero-desktop.webp" type="image/webp" width="1440" height="810" />
          <source media="(min-width: 769px)" srcSet="/img/hero-desktop.jpg" type="image/jpeg" width="1440" height="810" />
          <source srcSet="/img/hero-mobile.avif" type="image/avif" width="800" height="450" />
          <source srcSet="/img/hero-mobile.webp" type="image/webp" width="800" height="450" />
          <img
            src="/img/hero-mobile.jpg"
            alt="GoVans.ie branded van ready for a Dublin house move"
            width="800"
            height="450"
            fetchPriority="high"
            decoding="async"
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-[#0B1E36]/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left - headline, phone, CTAs */}
            <div>
              <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.08] mb-5">
                Moving Services<br />Dublin
              </h1>
              <p data-testid="hero-services" className="text-lg md:text-xl text-white/80 leading-relaxed mb-4 max-w-2xl font-medium">
                House Moves &bull; Man With a Van &bull; Furniture Delivery &bull; Office Moves &bull; Same-Day Service
              </p>
              <a href="tel:+353874199541" data-testid="hero-phone-display" className="inline-flex items-center gap-3 text-2xl sm:text-3xl font-extrabold text-white mb-8 hover:text-[#0055FF] transition-colors">
                <Phone className="w-7 h-7" />
                087 419 9541
              </a>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="tel:+353874199541" data-testid="hero-cta-call" className="flex-1 sm:flex-none">
                  <Button className="w-full sm:w-auto rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white text-base font-bold px-8 py-6 gap-2">
                    <Phone className="w-5 h-5" /> Call Now
                  </Button>
                </a>
                <a href="https://wa.me/353874199541?text=Hi%20GoVan!%20I%20need%20a%20van%20today%20in%20Dublin." target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="flex-1 sm:flex-none">
                  <Button className="w-full sm:w-auto rounded-sm bg-[#25D366] hover:bg-[#1fb855] text-white text-base font-bold px-8 py-6 gap-2">
                    <MessageCircle className="w-5 h-5" /> WhatsApp Us
                  </Button>
                </a>
              </div>
              <div data-testid="hero-trust-points" className="grid grid-cols-2 gap-3">
                {[
                  { icon: Zap, text: "Same-Day Service" },
                  { icon: BadgeEuro, text: "From \u20ac60" },
                  { icon: CalendarCheck, text: "7 Days a Week" },
                  { icon: ShieldCheck, text: "Commercially Insured" },
                ].map((tp) => (
                  <div key={tp.text} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-2.5 rounded-sm">
                    <tp.icon className="w-4 h-4 text-[#0055FF] shrink-0" strokeWidth={2} />
                    <span className="text-white text-xs sm:text-sm font-semibold">{tp.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - quick quote form */}
            <div data-testid="hero-quote-form" className="bg-white p-6 md:p-8 shadow-2xl">
              <h2 className="text-xl font-extrabold text-[#0A0A0A] mb-1">Get a Free Quote</h2>
              <p className="text-sm text-[#475569] mb-5">Fill in &amp; send via WhatsApp — instant reply</p>
              <form onSubmit={handleQuote} className="space-y-3">
                <Input data-testid="hero-quote-name" aria-label="Your name" placeholder="Your name" value={quoteData.name} onChange={(e) => setQuoteData({ ...quoteData, name: e.target.value })} className="rounded-sm border-black/10 h-11 focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <Input data-testid="hero-quote-phone" aria-label="Phone number" placeholder="Phone number" type="tel" value={quoteData.phone} onChange={(e) => setQuoteData({ ...quoteData, phone: e.target.value })} className="rounded-sm border-black/10 h-11 focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <select
                  data-testid="hero-quote-service"
                  aria-label="Service required"
                  value={quoteData.service}
                  onChange={(e) => setQuoteData({ ...quoteData, service: e.target.value })}
                  required
                  className={`w-full rounded-sm border border-black/10 bg-white h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent ${quoteData.service ? "text-[#0A0A0A]" : "text-[#9CA3AF]"}`}
                >
                  <option value="" disabled>Service required</option>
                  <option value="House Move">House Move</option>
                  <option value="Apartment Move">Apartment Move</option>
                  <option value="Man With a Van">Man With a Van</option>
                  <option value="Furniture Delivery">Furniture Delivery</option>
                  <option value="Office Move">Office Move</option>
                  <option value="Storage Move">Storage Move</option>
                  <option value="IKEA Collection / Delivery">IKEA Collection / Delivery</option>
                  <option value="Rubbish Removal">Rubbish Removal</option>
                  <option value="Other">Other</option>
                </select>
                <Input data-testid="hero-quote-pickup" aria-label="Pickup location" placeholder="Pickup location" value={quoteData.pickup} onChange={(e) => setQuoteData({ ...quoteData, pickup: e.target.value })} className="rounded-sm border-black/10 h-11 focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <Input data-testid="hero-quote-dropoff" aria-label="Drop-off location" placeholder="Drop-off location" value={quoteData.dropoff} onChange={(e) => setQuoteData({ ...quoteData, dropoff: e.target.value })} className="rounded-sm border-black/10 h-11 focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <Input data-testid="hero-quote-items" aria-label="What you need moved" placeholder="What do you need moved? (e.g. sofa, boxes)" value={quoteData.items} onChange={(e) => setQuoteData({ ...quoteData, items: e.target.value })} className="rounded-sm border-black/10 h-11 focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <Input data-testid="hero-quote-date" aria-label="Preferred move date" placeholder="Preferred date (e.g. Tomorrow, Monday, 20 Dec)" value={quoteData.date} onChange={(e) => setQuoteData({ ...quoteData, date: e.target.value })} className="rounded-sm border-black/10 h-11 focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <ConsentCheckbox
                  id="hero-quote-consent"
                  checked={quoteConsent}
                  onChange={setQuoteConsent}
                  text="I agree to be contacted about my quote."
                />
                {/* Primary: WhatsApp send */}
                <a
                  href={buildWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-quote-whatsapp"
                  className="block"
                  onClick={(e) => {
                    if (!quoteData.name || !quoteData.phone || !quoteData.service) {
                      e.preventDefault();
                      toast.error("Please enter your name, phone and service required.");
                      return;
                    }
                    setHeroReviewPrompt(true);
                  }}
                >
                  <Button type="button" className="w-full rounded-sm bg-[#25D366] hover:bg-[#1fb855] text-white font-bold h-12 text-base gap-2">
                    <MessageCircle className="w-5 h-5" /> Send via WhatsApp — Instant Reply
                  </Button>
                </a>
                {/* Secondary: email/call back */}
                <Button type="submit" disabled={quoteSubmitting} data-testid="hero-quote-submit" className="w-full rounded-sm bg-[#0A0A0A] hover:bg-[#333] text-white font-semibold h-10 text-sm">
                  {quoteSubmitting ? "Sending..." : "Or submit & we'll call you back"}
                </Button>
              </form>
              <p className="text-xs text-[#475569] text-center mt-3" data-testid="hero-quote-guide-note">
                For the most accurate fixed quote, please share a full item list, approximate number of boxes/bags, access details and photos where possible.
              </p>
              <p className="text-xs text-[#475569] text-center mt-1">Fastest response via WhatsApp</p>
              <GoogleReviewPrompt
                visible={heroReviewPrompt}
                onDismiss={() => setHeroReviewPrompt(false)}
                testid="hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== URGENCY SECTION ===== */}
      <section data-testid="urgency-section" className="py-16 md:py-20 bg-[#0B1E36] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,107,0,0.3) 10px, rgba(255,107,0,0.3) 20px)" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF6B00] text-white text-xs font-extrabold uppercase tracking-[0.15em] px-4 py-2 rounded-sm mb-6">
                <Zap className="w-4 h-4" fill="currentColor" strokeWidth={0} /> Fast Response
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4">
                Need a Van Urgently?
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                Last-minute move or urgent delivery? We provide fast-response van service across Dublin — often within <strong className="text-white">1-2 hours</strong>. When time matters, we prioritise your job and get it done fast, without delays.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  "Fast 1-2 minutes response time",
                  "Same-day service guaranteed*",
                  "Priority booking available",
                  "Last-minute moves & urgent deliveries",
                  "Skip the queue with priority service",
                  "Available 7 days a week",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#FF6B00] shrink-0" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/40 text-xs mb-6">*Subject to availability</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="tel:+353874199541" data-testid="urgency-cta-call">
                  <Button className="rounded-sm bg-[#FF6B00] hover:bg-[#e55f00] text-white text-base font-bold px-8 py-6 gap-2">
                    <Phone className="w-5 h-5" /> Book Emergency Service Now
                  </Button>
                </a>
                <a href="https://wa.me/353874199541?text=URGENT%20-%20I%20need%20a%20van%20NOW%20in%20Dublin" target="_blank" rel="noopener noreferrer" data-testid="urgency-cta-wa">
                  <Button className="rounded-sm bg-[#25D366] hover:bg-[#1fb855] text-white text-base font-bold px-8 py-6 gap-2">
                    <MessageCircle className="w-5 h-5" /> WhatsApp for Immediate Response
                  </Button>
                </a>
              </div>
            </div>

            {/* Right - premium pricing */}
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-sm border border-white/10 p-8">
                <h3 className="font-extrabold text-xl text-white mb-6 flex items-center gap-2">
                  <BadgeEuro className="w-6 h-6 text-[#FF6B00]" /> Priority Pricing
                </h3>
                <div className="space-y-5">
                  <div className="border-b border-white/10 pb-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-[#FF6B00]" fill="currentColor" strokeWidth={0} />
                        <span className="font-bold text-white text-base">Priority Same-Day Service</span>
                      </div>
                      <span className="text-2xl font-extrabold text-[#FF6B00]">+&euro;50</span>
                    </div>
                    <p className="text-white/50 text-sm">Your job jumps the queue. Guaranteed same-day pickup and delivery.</p>
                  </div>
                  <div className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-red-400" fill="currentColor" strokeWidth={0} />
                        <span className="font-bold text-white text-base">Express / Immediate Response</span>
                      </div>
                      <span className="text-2xl font-extrabold text-red-400">+&euro;70-80</span>
                    </div>
                    <p className="text-white/50 text-sm">Under 5 minute response. We drop everything and come to you. Fastest service in Dublin.</p>
                  </div>
                </div>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-white/40 text-xs">Priority fees are added on top of standard service rates. Standard rates still apply (from &euro;60).</p>
                </div>
              </div>
              <div className="bg-[#FF6B00]/20 border border-[#FF6B00]/30 p-4 flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#FF6B00] shrink-0" fill="currentColor" strokeWidth={0} />
                <p className="text-white/90 text-sm font-medium">Faster service = higher price. Standard service is still available at normal rates.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES OVERVIEW ===== */}
      <section data-testid="services-overview" className="py-20 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">What We Do</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight mb-4">Every Kind of Move, Covered</h2>
          <p className="text-[#475569] text-base md:text-lg mb-12 max-w-xl">
            Whatever you need shifted in Dublin, we&apos;ve got a van and a plan for it.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES_PREVIEW.map((s, i) => {
              const Icon = SERVICES_ICONS[s.icon];
              return (
                <Link
                  key={s.title}
                  to="/services"
                  data-testid={`service-card-${i}`}
                  className="group bg-white border border-black/10 p-8 transition-all hover:-translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)]"
                >
                  <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center rounded-sm mb-5">
                    <Icon className="w-6 h-6 text-[#0055FF]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-bold text-lg text-[#0A0A0A] mb-2">{s.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed mb-4">{s.desc}</p>
                  <span className="text-[#0055FF] text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
          {/* Helper text */}
          <div className="mt-8 bg-white border border-[#25D366]/20 p-5 flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-[#25D366] shrink-0" />
            <p className="text-sm text-[#475569]">Not sure which service you need? <a href="https://wa.me/353874199541?text=Hi%20GoVan!%20I'm%20not%20sure%20which%20service%20I%20need.%20Can%20you%20help?" target="_blank" rel="noopener noreferrer" className="font-bold text-[#25D366] hover:underline">Message us on WhatsApp</a> and we&apos;ll guide you.</p>
          </div>
        </div>
      </section>

      {/* ===== PHOTO GALLERY — Removed Feb 2026 per brand decision.
            Reason: the candid "real job" photos (v1-v4) read as unpolished for higher-value customers.
            The branded GoVans van is showcased professionally in <OurVanSection /> immediately below. ===== */}

      {/* ===== OUR BRANDED VAN ===== */}
      <OurVanSection />

      {/* ===== WHY CHOOSE US ===== */}
      <section data-testid="why-choose-us" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
            {[
              { number: "Local", label: "Dublin & Kildare" },
              { number: "1-2 min", label: "Response Time" },
              { number: "7", label: "Days a Week" },
              { number: "Insured", label: "Commercially Insured" },
            ].map((s) => (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="bg-[#F8F9FA] border border-black/5 p-6 text-center">
                <span className="text-3xl sm:text-4xl font-extrabold text-[#0055FF] block">{s.number}</span>
                <span className="text-sm text-[#475569] mt-1 block">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">Why GoVan?</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight mb-6">
                Dublin&apos;s Fast, Reliable Man With a Van
              </h2>
              <div className="space-y-6">
                {[
                  { icon: Zap, title: "At Your Door in 1-2 Hours", desc: "Same-day service, 7 days a week. Call in the morning, moved by the afternoon. No waiting around for days." },
                  { icon: BadgeEuro, title: "Competitive Fixed Prices", desc: "Small jobs from \u20ac60. We provide a fixed quote based on the items, distance, property access and number of movers required. Competitive fixed prices with no hidden charges." },
                  { icon: ShieldCheck, title: "Commercially Insured & Careful", desc: "Every item wrapped in protective blankets. Your belongings treated like our own. We are commercially insured for peace of mind." },
                  { icon: Smile, title: "Friendly, Reliable Lads", desc: "Read our reviews — customers love our team. Professional, on time, and always happy to help." },
                  { icon: Truck, title: "One Van Does It All", desc: "House moves, furniture, IKEA, marketplace pickups, rubbish removal. One call, one van, sorted." },
                  { icon: CalendarCheck, title: "Open 7 Days a Week", desc: "Evenings, weekends, bank holidays — we're here when you need us. Book online or call anytime." },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center rounded-sm shrink-0 mt-0.5">
                      <item.icon className="w-5 h-5 text-[#0055FF]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-bold text-base text-[#0A0A0A] mb-1">{item.title}</h3>
                      <p className="text-[#475569] text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="/img/van2.webp"
                alt="GoVans branded Dublin moving van — professional man with a van service"
                width="960"
                height="1280"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0055FF] text-white p-6 hidden lg:block">
                <span className="text-3xl font-extrabold block">7 Days</span>
                <span className="text-sm font-medium text-white/80">Same-Day Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LIVE GOOGLE REVIEWS WIDGET ===== */}
      <GoogleReviewsWidget />

      {/* ===== TRUST / REAL REVIEWS ===== */}
      <section data-testid="testimonials-section" className="py-20 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Header */}
          <div className="max-w-3xl mb-12">
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">Reviews</span>
            <h2 data-testid="testimonials-heading" className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight mb-4">
              Trusted Dublin Removal Service
            </h2>
            <p className="text-[#475569] text-base md:text-lg leading-relaxed">
              Every review is on our Google Business Profile — verified by Google, written by real Dublin customers. No paid testimonials, no fake names, no inflated numbers.
            </p>
          </div>

          {/* Trust badges — icons only, no fake numbers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              {
                icon: ShieldCheck,
                title: "Verified by Google",
                desc: "All reviews live on our Google Business Profile and are independently verified by Google.",
              },
              {
                icon: Smile,
                title: "Owner-Operated",
                desc: "Independent Dublin business. You speak directly with the person doing your job — not a call centre.",
              },
              {
                icon: Truck,
                title: "Commercially Insured",
                desc: "We hold public liability insurance and every item is wrapped in protective blankets. Note: public liability and Goods in Transit are separate insurances — ask us for the exact cover you need before booking.",
              },
            ].map((badge) => (
              <div
                key={badge.title}
                data-testid={`trust-pillar-${badge.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-white border border-black/10 p-7 flex flex-col"
              >
                <div className="w-11 h-11 bg-[#0055FF]/10 flex items-center justify-center rounded-sm mb-5">
                  <badge.icon className="w-5 h-5 text-[#0055FF]" strokeWidth={1.5} />
                </div>
                <h3 className="font-bold text-lg text-[#0A0A0A] mb-2">{badge.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed flex-1">{badge.desc}</p>
              </div>
            ))}
          </div>

          {/* Read on Google CTA */}
          <div data-testid="google-reviews-cta" className="bg-white border border-black/10 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              {/* Google "G" mark */}
              <div className="w-12 h-12 bg-white border border-black/10 rounded-full flex items-center justify-center shrink-0" aria-hidden>
                <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.28 1.42-1.07 2.62-2.28 3.42v2.84h3.69c2.16-1.99 3.61-4.93 3.61-8.5z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.69-2.84c-1.02.69-2.32 1.09-4.24 1.09-3.26 0-6.02-2.2-7.01-5.16H1.18v3.24C3.15 21.31 7.27 24 12 24z"/>
                  <path fill="#FBBC05" d="M4.99 14.18A7.2 7.2 0 0 1 4.62 12c0-.76.13-1.49.37-2.18V6.58H1.18A11.99 11.99 0 0 0 0 12c0 1.93.46 3.76 1.18 5.42l3.81-3.24z"/>
                  <path fill="#EA4335" d="M12 4.84c1.77 0 3.35.61 4.6 1.81l3.27-3.27C17.95 1.5 15.24.5 12 .5 7.27.5 3.15 3.19 1.18 6.58l3.81 3.24C5.98 7.04 8.74 4.84 12 4.84z"/>
                </svg>
              </div>
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#475569] mb-1">Real Customers</p>
                <h3 className="font-extrabold text-xl text-[#0A0A0A]">See our reviews on Google</h3>
                <p className="text-sm text-[#475569] mt-1">Verified Dublin moves — house removals, IKEA runs, same-day jobs.</p>
              </div>
            </div>
            <a
              href="https://maps.app.goo.gl/z3aZDGowkqnnBQ9S8?g_st=ic"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="read-google-reviews-btn"
              className="shrink-0"
            >
              <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold px-6 py-5 gap-2 text-base">
                Read our Google Reviews <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ===== LATEST FROM THE BLOG ===== */}
      <section data-testid="home-blog-teaser" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">From the Blog</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight">Dublin Moving Tips &amp; Local Advice</h2>
            </div>
            <Link to="/blog" data-testid="home-blog-all-link" className="inline-flex items-center gap-1 text-sm font-bold text-[#0055FF] hover:underline shrink-0">
              View all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {POSTS_INDEX.slice(0, 3).map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`home-blog-card-${post.slug}`}
                className="group flex flex-col bg-white border border-black/10 hover:border-[#0055FF] transition-colors rounded-sm overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <BlogTile category={post.category} size="card" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#0055FF] mb-3">{post.category}</span>
                  <h3 className="font-extrabold text-base md:text-lg text-[#0A0A0A] leading-tight mb-3 group-hover:text-[#0055FF] transition-colors">{post.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed mb-5 flex-1">{post.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0055FF] mt-auto">
                    Read article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GOCLEANING CROSS-PROMO (related but separate business) ===== */}
      <GoCleaningPromo />

      {/* ===== CTA BANNER ===== */}
      <section data-testid="cta-banner" className="py-16 md:py-20 bg-[#0055FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-3">
            Need a Van Today?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-md mx-auto">
            Call now for a free quote. Same-day service available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+353874199541" data-testid="cta-call">
              <Button className="rounded-sm bg-white text-[#0055FF] hover:bg-white/90 text-lg font-bold px-10 py-7 gap-3">
                <Phone className="w-5 h-5" /> 087 419 9541
              </Button>
            </a>
            <a href="https://wa.me/353874199541?text=Hi%20GoVan!%20I%20need%20a%20van%20today." target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp">
              <Button className="rounded-sm bg-[#25D366] hover:bg-[#1fb855] text-white text-lg font-bold px-10 py-7 gap-3">
                <MessageCircle className="w-5 h-5" /> WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ===== SIMPLE CONTACT FORM ===== */}
      <section data-testid="quick-contact" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">Get a Quote</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight mb-6">
                Tell Us What You Need
              </h2>
              <p className="text-[#475569] text-base md:text-lg mb-8 leading-relaxed">
                Fill in your details and we&apos;ll call you back with a price — usually within 1-2 minutes.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Free, no-obligation quote",
                  "We call YOU back — no chasing",
                  "Same-day availability",
                  "No job too big or small",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#0055FF] shrink-0" />
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#475569] text-sm">Or call / WhatsApp us directly:</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <a href="tel:+353874199541">
                  <Button variant="outline" className="rounded-sm gap-2 border-black/10 font-bold hover:bg-[#0A0A0A] hover:text-white">
                    <Phone className="w-4 h-4" /> 087 419 9541
                  </Button>
                </a>
                <a href="https://wa.me/353874199541" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-sm gap-2 border-[#25D366] text-[#25D366] font-bold hover:bg-[#25D366] hover:text-white">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
            <form onSubmit={handleContact} data-testid="home-contact-form" className="bg-[#F8F9FA] border border-black/10 p-8">
              <h3 className="font-bold text-lg text-[#0A0A0A] mb-5">Quick Quote Request</h3>
              <div className="space-y-4">
                <Input data-testid="home-contact-name" aria-label="Your name" placeholder="Your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-sm border-black/10 bg-white focus:ring-2 focus:ring-[#0055FF] focus:border-transparent h-12" />
                <Input data-testid="home-contact-phone" aria-label="Phone number" placeholder="Phone number" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="rounded-sm border-black/10 bg-white focus:ring-2 focus:ring-[#0055FF] focus:border-transparent h-12" />
                <Textarea data-testid="home-contact-message" aria-label="What you need moved" placeholder="What do you need moved? (e.g. sofa from Rathmines to Drumcondra)" rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="rounded-sm border-black/10 bg-white focus:ring-2 focus:ring-[#0055FF] focus:border-transparent" />
                <ConsentCheckbox
                  id="home-contact-consent"
                  checked={contactConsent}
                  onChange={setContactConsent}
                  text="I agree to be contacted about my move."
                />
                <Button type="submit" disabled={submitting} data-testid="home-contact-submit" className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold h-12 text-base">
                  {submitting ? "Sending..." : "Get My Free Quote"}
                </Button>
                <p className="text-xs text-[#475569] text-center">We&apos;ll call you back within 1-2 minutes</p>
              </div>
              <GoogleReviewPrompt
                visible={contactReviewPrompt}
                onDismiss={() => setContactReviewPrompt(false)}
                testid="contact"
              />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
